import { useMemo } from "react"
import { ConditionGroup, Contact, ContactListResponse, NextPageParams, PaginationMetadata } from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { AllContactIdsResponse, fetchAllContactIds, fetchContact, fetchContacts } from "./api"

export function useFetchContacts(searchTerm?: string, bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  return useQuery({
    queryKey: ["contacts", searchTerm, bookIdOverride],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchContacts(context, undefined, undefined, searchTerm, undefined, true)
    },
  })
}

export function useInfiniteContacts(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () =>
      ({
        combinator: "and",
        conditions: completedConditions,
      }) as ConditionGroup,
    [completedConditions]
  )
  return useInfiniteQuery<
    ContactListResponse,
    Error,
    { contacts: Contact[]; pagination: PaginationMetadata },
    (string | ConditionGroup | undefined | { field?: string; direction?: "asc" | "desc" })[],
    NextPageParams | undefined
  >({
    queryKey: ["contacts", "infinite", searchTerm, computedFilterGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return fetchContacts(context, computedFilterGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      contacts: data.pages.flatMap((page) => page.contacts),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useFetchContact(id: string | null) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["contact", id],
    enabled: !!id,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!id) throw new Error("No id found")
      return fetchContact(context, id)
    },
  })
}

export function useFetchAllContactIds() {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  const computedFilterGroup = {
    combinator: "and",
    conditions: completedConditions,
  } as ConditionGroup

  return useQuery<AllContactIdsResponse>({
    queryKey: ["contacts", "all_ids", searchTerm, computedFilterGroup, viewSort],
    queryFn: async () => {
      const content = await getContext()
      if (!content) throw new Error("No context found")
      return fetchAllContactIds(content, computedFilterGroup, searchTerm)
    },
  })
}
